import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, withProps, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import * as moment from 'moment-timezone';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { State } from 'reducers';
import { connect } from 'react-redux';
import { UtcTime } from 'types';
import { Input } from 'ui/shared/Input';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const indentedStyle = {
    marginLeft: '2em',
};

const RawEndSpecific = ({
    endSpecific,
    endDate,
    endTime,
    handleChangeEndSpecific,
    handleChangeEndDate,
    handleChangeEndTime,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <Fragment>
        <div>
            <label>
                <input
                    type="radio"
                    name="manual-data-end"
                    id="manual-data-end-specific"
                    checked={ endSpecific }
                    onChange={ handleChangeEndSpecific }
                />
                { t('6.1241.0.77') }
            </label>
        </div>
        <div style={ indentedStyle }>
            <Input
                id="manual-data-end-date"
                value={ endDate }
                onChange={ handleChangeEndDate }
                maxLength={ 10 }
                placeholder={ t('WC_REPORT_SCHEDULE_DATE_PLACEHOLDER') }
            />
            <Input
                id="manual-data-end-time"
                value={ endTime }
                onChange={ handleChangeEndTime }
                maxLength={ 5 }
                placeholder={ t('WC_REPORT_SCHEDULE_TIME_PLACEHOLDER') }
            />
        </div>
    </Fragment>
;

type ChangeHandler = React.ChangeEventHandler<HTMLInputElement>;

type RecomposeProps = {
    endSpecific: boolean;
    endDate: string;
    endTime: string;
    handleChangeEndSpecific: ChangeHandler;
    handleChangeEndDate: ChangeHandler;
    handleChangeEndTime: ChangeHandler;
};
type _RecomposeProps = RecomposeProps & {
    _setEndDate: (s: string) => void;
    _setEndTime: (s: string) => void;
};
type StateProps = {
    _tzOffsetCalculator: (t: UtcTime) => number;
};

const parseEnd = (reportSettings: Readonly<ReportSettings>, what: 'date' | 'time', tzOffsetCalculator?: (t: UtcTime) => number): string => {
    const { end } = reportSettings;
    if (end <= 0) {
        return '';
    }
    const utcEnd = moment(end).utc();
    const localEnd = utcEnd.clone().utcOffset(tzOffsetCalculator!(utcEnd.valueOf()));
    switch (what) {
        case 'date':
            return localEnd.format('YYYY-MM-DD');

        case 'time':
            return localEnd.format('HH:mm');
    }
};

const updateEnd = ({ onChange, endDate, endTime, _tzOffsetCalculator }: Props & _RecomposeProps, force: boolean) => {
    const utcEnd = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD H:mm', true); // Note: not UTC yet...
    if (utcEnd.isValid()) {
        utcEnd.utcOffset(-_tzOffsetCalculator(utcEnd.valueOf()));
        onChange(reportSettings => reportSettings.setEnd(utcEnd.valueOf()));
    }
    else if (force) {
        const localToday = moment().endOf('day');
        const utcToday = localToday.clone().utcOffset(-_tzOffsetCalculator(localToday.valueOf()));
        onChange(reportSettings => reportSettings.setEnd(utcToday.valueOf()));
    }
};

const mapStateToProps = (state: State) => ({
    _tzOffsetCalculator: getTimezoneOffsetCalculator(state),
});

type Props = OwnProps & StateProps & RecomposeProps & TranslateProps;

export const EndSpecific = compose<Props, OwnProps>(
    setDisplayName('EndSpecific'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        endSpecific: reportSettings.end > 0,
    })),
    connect<StateProps, {}, {}>(mapStateToProps),
    withState('endDate', '_setEndDate', ({ reportSettings, _tzOffsetCalculator }: Props) =>
        parseEnd(reportSettings, 'date', _tzOffsetCalculator)
    ),
    withState('endTime', '_setEndTime', ({ reportSettings, _tzOffsetCalculator }: Props) =>
        parseEnd(reportSettings, 'time', _tzOffsetCalculator)
    ),
    withHandlers<Props & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeEndSpecific: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                updateEnd(props, true);
            }
        },
        handleChangeEndDate: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const endDate = e.target.value;
            props._setEndDate(endDate);
            updateEnd({ ...props, endDate }, false);
        },
        handleChangeEndTime: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const endTime = e.target.value;
            props._setEndTime(endTime);
            updateEnd({ ...props, endTime }, false);
        },
    }),
    lifecycle<Props & _RecomposeProps, null>({
        componentDidUpdate(oldProps: Props) {
            const { reportSettings, _setEndDate, _setEndTime, _tzOffsetCalculator } = this.props;
            if (oldProps.reportSettings.end !== reportSettings.end) {
                _setEndDate(parseEnd(reportSettings, 'date', _tzOffsetCalculator));
                _setEndTime(parseEnd(reportSettings, 'time', _tzOffsetCalculator));
            }
        },
    }),
)(RawEndSpecific);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/dateRange/EndSpecific.tsx